(() => {
  if (customElements.get('custom-box-builder')) return;

  class CustomBoxBuilder extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 0;
      this.steps = [];
      this.products = [];
    }

    connectedCallback() {
      this.form = this.querySelector('[data-builder-form]');
      if (!this.form) return;

      this.steps = [...this.querySelectorAll('[data-builder-step]')];
      this.backButton = this.querySelector('[data-builder-back]');
      this.nextButton = this.querySelector('[data-builder-next]');
      this.checkoutButton = this.querySelector('[data-builder-checkout]');
      this.status = this.querySelector('[data-builder-status]');
      this.progress = this.querySelector('[data-builder-progress]');
      this.progressTrack = this.querySelector('.custom-box-builder__progress');
      this.stepCount = this.querySelector('[data-builder-step-count]');
      this.products = this.readJson('[data-builder-products]');

      this.backButton?.addEventListener('click', () => this.setStep(this.currentStep - 1));
      this.nextButton?.addEventListener('click', () => this.goForward());
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.form.addEventListener('change', (event) => this.onSelectionChange(event));
      this.querySelectorAll('[data-builder-edit]').forEach((button) => {
        button.addEventListener('click', () => this.setStep(Number(button.dataset.builderEdit || 0)));
      });

      this.updatePlanCards();
      this.setStep(0);
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return [];
      try {
        const value = JSON.parse(node.textContent || '[]');
        return Array.isArray(value) ? value : [];
      } catch (error) {
        console.error('LANCHAUS builder JSON could not be read.', error);
        return [];
      }
    }

    normalize(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[–—]/g, '-')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
          minimumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    selectedInput(name) {
      return this.form.querySelector(`input[name="${name}"]:checked`);
    }

    selection() {
      const sizeInput = this.selectedInput('dogSize');
      const planInput = this.selectedInput('plan');
      return {
        dogName: String(this.form.elements.dogName?.value || '').trim(),
        dogSize: sizeInput?.value || '',
        sizeNote: sizeInput?.dataset.sizeNote || '',
        plan: planInput?.value || '',
      };
    }

    findProduct(tier) {
      const expected = this.normalize(tier);
      return this.products.find((product) => this.normalize(product.tier) === expected) || null;
    }

    findVariant(product, size) {
      if (!product) return null;
      const expected = this.normalize(size);
      return (product.variants || []).find((variant) =>
        (variant.options || []).some((option) => this.normalize(option) === expected),
      ) || null;
    }

    findSellingPlan(variant) {
      return (variant?.sellingPlans || [])[0] || null;
    }

    getPurchaseSelection(planOverride) {
      const selected = this.selection();
      const plan = planOverride || selected.plan;
      const product = this.findProduct(plan);
      const variant = this.findVariant(product, selected.dogSize);
      const sellingPlan = this.findSellingPlan(variant);
      return { ...selected, plan, product, variant, sellingPlan };
    }

    onSelectionChange(event) {
      if (event.target?.name === 'dogSize') this.updatePlanCards();
      if (event.target?.name === 'dogSize' || event.target?.name === 'plan') this.updateReview();
    }

    updatePlanCards() {
      this.querySelectorAll('input[name="plan"]').forEach((input) => {
        const purchase = this.getPurchaseSelection(input.value);
        const card = input.closest('.custom-box-builder__option');
        const price = card?.querySelector('[data-plan-price]');
        const caption = card?.querySelector('[data-plan-caption]');
        const configured = Boolean(purchase.product?.id && purchase.variant && purchase.sellingPlan);

        if (configured) {
          if (price) price.textContent = `${this.formatMoney(purchase.sellingPlan.price)} / month`;
          if (caption) caption.textContent = 'Renews monthly · Cancel anytime';
          input.disabled = !purchase.variant.available;
          card?.classList.remove('is-unavailable');
        } else {
          if (price) price.textContent = 'Not configured';
          if (caption) caption.textContent = 'Select the product and add a no-discount monthly plan';
          input.disabled = true;
          card?.classList.add('is-unavailable');
        }
      });

      const checked = this.selectedInput('plan');
      if (checked?.disabled) {
        const firstAvailable = [...this.form.querySelectorAll('input[name="plan"]')].find((input) => !input.disabled);
        if (firstAvailable) firstAvailable.checked = true;
      }
    }

    validateCurrentStep() {
      if (this.currentStep !== 0) return true;
      const name = String(this.form.elements.dogName?.value || '').trim();
      const error = this.querySelector('[data-error-for="dogName"]');
      if (!name) {
        if (error) error.textContent = "Enter your dog's name to continue.";
        this.form.elements.dogName?.focus();
        return false;
      }
      if (error) error.textContent = '';
      return true;
    }

    goForward() {
      if (!this.validateCurrentStep()) return;
      if (this.currentStep === 1) this.updatePlanCards();
      this.setStep(Math.min(this.currentStep + 1, this.steps.length - 1));
    }

    setStep(index) {
      if (!this.steps.length) return;
      this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, stepIndex) => {
        const active = stepIndex === this.currentStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });

      const position = ((this.currentStep + 1) / this.steps.length) * 100;
      if (this.progress) this.progress.style.width = `${position}%`;
      if (this.progressTrack) this.progressTrack.setAttribute('aria-valuenow', String(this.currentStep + 1));
      if (this.stepCount) this.stepCount.textContent = `Step ${this.currentStep + 1} of ${this.steps.length}`;
      if (this.backButton) this.backButton.hidden = this.currentStep === 0;
      if (this.nextButton) this.nextButton.hidden = this.currentStep === this.steps.length - 1;
      if (this.checkoutButton) this.checkoutButton.hidden = this.currentStep !== this.steps.length - 1;

      if (this.currentStep === 2) this.updatePlanCards();
      if (this.currentStep === this.steps.length - 1) this.updateReview();
      this.showStatus('');
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    updateReview() {
      const purchase = this.getPurchaseSelection();
      this.querySelectorAll('[data-dog-name]').forEach((node) => {
        node.textContent = purchase.dogName || 'Your dog';
      });
      this.setReviewValue('dogName', purchase.dogName || '—');
      this.setReviewValue('dogSize', purchase.dogSize || '—');
      this.setReviewValue('sizeNote', purchase.sizeNote || '—');
      this.setReviewValue('plan', purchase.plan || '—');
      this.setReviewValue('billing', purchase.sellingPlan ? `${this.formatMoney(purchase.sellingPlan.price)} every month` : 'Plan not configured');

      const productTitle = this.querySelector('[data-selected-product-title]');
      const variantLabel = this.querySelector('[data-selected-variant]');
      const priceLabel = this.querySelector('[data-selected-price]');
      if (productTitle) productTitle.textContent = purchase.product?.title || 'Choose four products in the theme editor';
      if (variantLabel) variantLabel.textContent = `${purchase.dogSize || 'Choose a size'} · ${purchase.plan || 'Choose a plan'}`;
      if (priceLabel) priceLabel.textContent = purchase.sellingPlan ? `${this.formatMoney(purchase.sellingPlan.price)} / month` : '—';

      const productImage = this.querySelector('.custom-box-builder__product-image');
      const selectedImage = purchase.variant?.image || purchase.product?.image;
      if (productImage && selectedImage) productImage.src = selectedImage;

      let problem = '';
      if (this.dataset.productReady !== 'true') {
        problem = 'Choose all four subscription products in the theme editor.';
      } else if (!purchase.product?.id) {
        problem = `The product for “${purchase.plan}” is not configured.`;
      } else if (!purchase.variant) {
        problem = `The “${purchase.plan}” product is missing the “${purchase.dogSize}” variant.`;
      } else if (!purchase.variant.available) {
        problem = `The “${purchase.dogSize}” variant is currently unavailable.`;
      } else if (!purchase.sellingPlan) {
        problem = `Add a no-discount monthly Shopify Subscriptions plan to the “${purchase.plan}” product.`;
      }

      if (this.checkoutButton) this.checkoutButton.disabled = Boolean(problem);
      this.showStatus(problem);
    }

    setReviewValue(key, value) {
      const node = this.querySelector(`[data-review="${key}"]`);
      if (node) node.textContent = value;
    }

    showStatus(message, type = 'error') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.statusType = message ? type : '';
    }

    setLoading(loading) {
      if (!this.checkoutButton) return;
      this.checkoutButton.disabled = loading;
      this.checkoutButton.classList.toggle('is-loading', loading);
    }

    async submit(event) {
      event.preventDefault();
      const purchase = this.getPurchaseSelection();
      if (!purchase.product || !purchase.variant || !purchase.sellingPlan || !purchase.variant.available) {
        this.updateReview();
        return;
      }

      const item = {
        id: purchase.variant.id,
        quantity: 1,
        selling_plan: purchase.sellingPlan.id,
        properties: {
          'Dog Name': purchase.dogName,
          'Dog Size': purchase.dogSize,
          'Recommended Fit': purchase.sizeNote,
          'Subscription Tier': purchase.plan,
          'Billing Frequency': 'Every month',
          'Automatic Renewal': 'Yes, until canceled',
        },
      };

      if (this.dataset.previewMode === 'true') {
        window.alert(`Preview checkout\nProduct: ${purchase.product.title}\nVariant: ${item.id}\nSelling plan: ${item.selling_plan}`);
        return;
      }

      this.setLoading(true);
      this.showStatus('');
      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [item] }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.description || body.message || 'The subscription could not be added to the cart.');
        }
        window.location.assign(this.dataset.checkoutUrl || '/checkout');
      } catch (error) {
        this.showStatus(error.message || 'Something went wrong. Please try again.');
        this.setLoading(false);
      }
    }
  }

  customElements.define('custom-box-builder', CustomBoxBuilder);
})();
